import type { ReactNode } from 'react';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateTitle,
  Link,
} from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';

/** Sensitive-attribute filters hide results the viewer isn't allowed to see. */
export function HiddenResultsEmptyState() {
  return (
    <EmptyState>
      <EmptyStateTitle icon="warning">Results are hidden</EmptyStateTitle>
      <EmptyStateDescription>
        You do not have permission to view results when filtering by sensitive attributes.
      </EmptyStateDescription>
    </EmptyState>
  );
}

type NoFiltersEmptyStateProps = {
  /** The "Add people" call to action — supplied by the page, which owns the create flow entry. */
  action?: ReactNode;
};

/** The zero-results state when no search or filter is active. */
export function NoFiltersEmptyState({ action }: NoFiltersEmptyStateProps) {
  const taxonomy = useProductTaxonomy();
  return (
    <EmptyState>
      <EmptyStateTitle icon="person">Build your audience</EmptyStateTitle>
      <EmptyStateDescription>
        Add {taxonomy.term('people.plural')} to see their attributes, events performed, messages received, and more. Use
        these profiles to personalize your messages.
      </EmptyStateDescription>
      {action == null ? null : <EmptyStateActions>{action}</EmptyStateActions>}
      {/* `as="div"`: the footer defaults to a <p>, which can't contain the link's icon wrapper. */}
      <EmptyStateFooter as="div">
        <Link href="https://docs.customer.io/getting-started-people" variant="secondary" isExternal icon="help">
          Learn more about {taxonomy.term('people.titlePlural')}
        </Link>
      </EmptyStateFooter>
    </EmptyState>
  );
}
